import React, { useState, useEffect, useRef } from "react";
import { useLocation, useNavigate, Link } from "react-router";
import { toast } from "react-toastify";
import Loader from "../../shared/loader/loader";
import {
  getContentArea,
  deleteContentArea,
} from "../../../api/contentArea.service";
import SelectPageSize from "../../shared/common/pageSizeDropdown";
import AddandEditSubjectModal from "./manageContent";
import DeleteModal from "../../shared/common/deleteModal";
import { IContent, IContentFilters } from "../../../model/interface/content";
import Pagination from "../../shared/common/pagination";
import {
  AddIcon,
  EditIcon,
  DeleteIcon,
  FilterIcon,
  ResetIcon,
  CaretUpSolidIcon,
  CaretDownSolidIcon,
  CSVIcon,
} from "../../../assets/icons/index";
import Constant from "../../../utils/constant";
import ExportToCSV from "../../shared/common/exportToCsv";
import AllowedTo from "../../shared/common/rbac";
import Permissions from "../../../utils/permissions";
import AccessDenied from "../../shared/common/accessDenied";

function manageModalHandler() {
  if (useLocation().search) {
    const value = new URLSearchParams(useLocation().search);
    const thisParam = value.get("openAddScreen");
    return thisParam !== undefined;
  }

  return false;
}

const Content: React.FC = () => {
  const initialState = {
    contentAreaId: 0,
    name: "",
    abbreviation: "",
    pageNumber: 1,
    pageSize: 10,
    SortDir: "asc",
    OrderByColumn: "name",
  };
  const initialValue = {
    contentAreaId: 0,
    name: "",
    abbreviation: "",
  };
  const manageModal = manageModalHandler();
  const [showPostSubjectModal, setShowPostSubjectModal] =
    useState<boolean>(false);
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
  const [newContentArea, setNewContentArea] = useState<any>([]);
  const [editNewSection, setNewEditSection] = useState<any>(null);
  const [deleteContent, setDeleteContent] = useState<IContent>(initialValue);
  const [loading, setLoading] = useState<boolean>(false);
  const [filterInput, setFilterInput] = useState<IContentFilters>(initialState);
  const [pageNumber, setPageNumber] = useState<any>(1);
  const [pageSize, setPageSize] = useState<number>(10);
  const [totalPages, setTotalPages] = useState<number>(0);
  const [totalRecords, setTotalRecords] = useState<number>(0);
  const [sortCol, setSortCol] = useState<string>("");
  const [sortByDesc, setSortByDesc] = useState<boolean>(false);
  const navigate = useNavigate();
  const paginationRef: any = useRef(undefined);
  const pageSizeRef: any = useRef(undefined);
  const [isLoaded, setIsLoaded] = useState<boolean>(false);
  const [isFilter, setIsFilter] = useState<boolean>(false);
  const childRef: any = useRef(undefined);

  useEffect(() => {
    if (manageModal) {
      setShowPostSubjectModal(true);
    }
  }, [manageModal]);

  useEffect(() => {
    content();
  }, []);

  useEffect(() => {
    if (pageSize * (pageNumber - 1) < totalRecords) {
      content();
    }
  }, [pageSize, pageNumber]);

  useEffect(() => {
    if (isLoaded && !isFilter) {
      content();
    }
  }, [pageNumber]);

  useEffect(() => {
    if (pageNumber === 1 && !isFilter) {
      content();
      setIsLoaded(true);
    } else {
      paginationRef?.current?.onReset();
    }
  }, [pageSize]);

  const closePostSubjectModal = () => {
    setShowPostSubjectModal(false);
    navigate("/content");
  };

  const handleRedirect = (content?: IContent) => {
    navigate(
      content
        ? Constant.EDITCONTENT.replace(":id", content.contentAreaId.toString())
        : Constant.ADDCONTENT,
      { state: { objectToEdit: content } }
    );
  };

  const DeleteContentArea = (e, id) => {
    e.preventDefault();
    setShowDeleteModal(true);
    const deleteContent = newContentArea.find(
      (content) => content.contentAreaId === id
    );
    if (deleteContent) setDeleteContent(deleteContent);
  };

  const deleteRecordHandler = async () => {
    const result = (await deleteContentArea(deleteContent.contentAreaId)).data;
    if (result.status === "Error") {
      toast.error(result.message);
    } else {
      contentDataReload();
      toast.success("Deleted Successfully");
    }

    setShowDeleteModal(false);
  };

  const closeDeleteModal = () => {
    setShowDeleteModal(false);
  };

  const filterInputChangeHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
    const target = e.target;
    const value = target.value;
    const name = target.name;
    setFilterInput({
      ...filterInput,
      [name]: value,
    });

    if (e.target.value === "") {
      content();
    }
  };

  const filterHandler = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    content(true);
  };

  const handleSortBy = (column: string) => {
    setSortByDesc(!sortByDesc);
    setSortCol(column);
    bindAPI(column);
  };

  const bindAPI = (column: string) => {
    getContentArea(
      pageSize,
      pageNumber,
      sortCol,
      sortByDesc ? "desc" : "asc"
    ).then((response) => {
      setNewContentArea(response.data.data);
    });
  };

  async function content(isFilter?, isReset?) {
    if (isFilter) setIsFilter(true);

    setLoading(true);

    const response = getContentArea(
      isFilter || isReset ? initialState.pageSize : pageSize,
      isFilter || isReset ? initialState.pageNumber : pageNumber,
      sortCol,
      sortByDesc ? "desc" : "asc",
      isFilter ? filterInput.contentAreaId : initialState.contentAreaId,
      isFilter ? filterInput.name : initialState.name,
      isFilter ? filterInput.abbreviation : initialState.abbreviation
    );

    const data = (await response).data;
    setNewContentArea(data.data);
    setTotalPages(Math.ceil(data.totalRecords / pageSize));
    setTotalRecords(data.totalRecords);
    if (isFilter) {
      paginationRef?.current?.onReset();
      pageSizeRef?.current?.onReset();
      setIsFilter(false);
    }
    setLoading(false);
  }

  const contentDataReload = () => {
    content();
  };

  const resetClickHandler = () => {
    content(false, true);
    setFilterInput({
      ...initialState,
    });
    paginationRef?.current?.onReset();
    pageSizeRef?.current?.onReset();
  };

  const exportFn = async () => {
    setLoading(true);
    const response = getContentArea(
      9999,
      1,
      sortCol,
      sortByDesc ? "desc" : "asc",
      filterInput.contentAreaId,
      filterInput.name,
      filterInput.abbreviation
    );

    const data = (await response).data;
    setLoading(false);

    if (data && data.data && data.data.length > 0) {
      childRef?.current?.onDownload(data.data, "contentArea.csv");
    } else {
      toast.warning("No records to download");
    }
  };

  return (
    <AllowedTo
      perform={Permissions.content_area_view}
      no={() => <AccessDenied />}
    >
      {loading && <Loader />}

      {showPostSubjectModal && (
        <AddandEditSubjectModal
          closePostSubjectModal={closePostSubjectModal}
          editNewSection={editNewSection}
          showPostSubjectModal={showPostSubjectModal}
          contentDataReload={contentDataReload}
          allContentData={newContentArea}
        />
      )}

      {showDeleteModal && (
        <DeleteModal
          closeDeleteModal={closeDeleteModal}
          deleteRecord={deleteRecordHandler}
        />
      )}

      <div className="flex-1 overflow-auto">
        <div className=" h-auto min-h-half w-full rounded-bl-3xl bg-site-teal px-8 py-8 pt-0 pb-14 mt-7 ">
          <div className="relative  py-1">
            <div className="overlay-image-main pointer-events-none fixed left-0 top-0 w-screen h-screen z-20"></div>
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-light text-gray-900">Content Area</h3>
              <AllowedTo perform={Permissions.content_area_create}>
                <button
                  type="button"
                  className="inline-flex items-center px-3 py-1 border border-transparent text-sm leading-4 rounded-md shadow-xs text-white bg-primary-color dark:text-white hover:bg-secondary-color focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-color hover:shadow-lg transsition ease-in-out duration-300"
                  onClick={() => handleRedirect()}
                >
                  <AddIcon className="w-6"></AddIcon>
                  Add Content Area
                </button>
              </AllowedTo>
            </div>
            <form action="#" onSubmit={filterHandler}>
              <div className="pt-6 border-b pb-6">
                <div className="grid grid-cols-12 gap-6">
                  <div className="col-span-2">
                    <label className="text-sm text-gray-700">
                      Content Area Id
                    </label>
                    <div className="contentArea">
                      <input
                        id="contentAreaId"
                        name="contentAreaId"
                        type="text"
                        autoComplete="off"
                        className="block w-full mt-1 border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-color focus:border-primary-color dark:focus:border-gray-500 dark:focus:ring-gray-500 dark:bg-custom-black dark:text-white sm:text-sm"
                        value={filterInput?.contentAreaId || ""}
                        onChange={filterInputChangeHandler}
                      />
                    </div>
                  </div>

                  <div className="col-span-2">
                    <label className="text-sm text-gray-700">Name</label>
                    <div className="contentArea">
                      <input
                        id="name"
                        name="name"
                        type="text"
                        autoComplete="off"
                        className="block w-full mt-1 border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-color focus:border-primary-color dark:focus:border-gray-500 dark:focus:ring-gray-500 dark:bg-custom-black dark:text-white sm:text-sm"
                        value={filterInput.name}
                        onChange={filterInputChangeHandler}
                      />
                    </div>
                  </div>
                  <div className="col-span-2 ">
                    <label className="text-sm text-gray-700">
                      Abbreviation
                    </label>
                    <div className="contentArea">
                      <input
                        id="abbreviation"
                        name="abbreviation"
                        type="text"
                        autoComplete="off"
                        className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-color focus:border-primary-color dark:focus:border-gray-500 dark:focus:ring-gray-500 dark:bg-custom-black dark:text-white sm:text-sm"
                        value={filterInput.abbreviation}
                        onChange={filterInputChangeHandler}
                      />
                    </div>
                  </div>
                </div>
                <div className="flex justify-start">
                  <button
                    type="submit"
                    className="mt-4 inline-flex items-center text-sm text-white dark:text-white gap-2 bg-primary-color hover:bg-secondary-color py-1.5 px-4 rounded-lg"
                  >
                    <FilterIcon className="w-3 h-3"></FilterIcon>
                    <span>Filter</span>
                  </button>
                  <button
                    type="button"
                    onClick={resetClickHandler}
                    className="mt-4 py-1.5 px-4 inline-flex ml-3 items-center text-sm rounded-lg text-gray-700 text-primary-color border border-gray-300 gap-2 bg-white hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-color "
                  >
                    <ResetIcon className="w-3 h-3"></ResetIcon>
                    <span>Reset Filter</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => exportFn()}
                    className="mt-4 py-1.5 px-4 inline-flex ml-auto items-center text-sm rounded-lg text-gray-700 text-primary-color border border-gray-300 gap-2 bg-white hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-color "
                  >
                    <CSVIcon className="w-3 h-3 dark:text-white stroke-current"></CSVIcon>
                    <span>Export</span>
                  </button>
                  <ExportToCSV ref={childRef} />
                </div>
              </div>
            </form>

            <div id="dataContainer" className="mt-4">
              <div className="flex flex-col mt-1">
                <div className="-my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
                  <div className="py-2 align-middle inline-block min-w-full sm:px-6 lg:px-8">
                    <div className="shadow overflow-hidden border-b border-gray-200 sm:rounded-lg">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-primary-color">
                          <tr>
                            <th
                              scope="col"
                              className="flex px-3 py-2 font-light text-sm text-white dark:text-white tracking-wider"
                            >
                              <span className="flex">
                                Id
                                <span className="cursor-pointer">
                                  {sortByDesc && sortCol === "contentAreaId" ? (
                                    <CaretUpSolidIcon
                                      onClick={() =>
                                        handleSortBy("contentAreaId")
                                      }
                                      className="w-5 h-5"
                                    ></CaretUpSolidIcon>
                                  ) : (
                                    <CaretDownSolidIcon
                                      onClick={() =>
                                        handleSortBy("contentAreaId")
                                      }
                                      className="w-5 h-5"
                                    ></CaretDownSolidIcon>
                                  )}
                                </span>
                              </span>
                            </th>
                            <th
                              scope="col"
                              className="px-3 py-2 font-light text-sm text-white dark:text-white tracking-wider"
                            >
                              <span className="flex">
                                Name
                                <span className="cursor-pointer">
                                  {sortByDesc && sortCol === "name" ? (
                                    <CaretUpSolidIcon
                                      onClick={() => handleSortBy("name")}
                                      className="w-5 h-5"
                                    ></CaretUpSolidIcon>
                                  ) : (
                                    <CaretDownSolidIcon
                                      onClick={() => handleSortBy("name")}
                                      className="w-5 h-5"
                                    ></CaretDownSolidIcon>
                                  )}
                                </span>
                              </span>
                            </th>
                            <th
                              scope="col"
                              className="flex px-3 py-2 font-light text-sm text-white dark:text-white tracking-wider"
                            >
                              <span className="flex">
                                Abbreviation
                                <span className="cursor-pointer">
                                  {sortByDesc && sortCol === "abbreviation" ? (
                                    <CaretUpSolidIcon
                                      onClick={() =>
                                        handleSortBy("abbreviation")
                                      }
                                      className="w-5 h-5"
                                    ></CaretUpSolidIcon>
                                  ) : (
                                    <CaretDownSolidIcon
                                      onClick={() =>
                                        handleSortBy("abbreviation")
                                      }
                                      className="w-5 h-5"
                                    ></CaretDownSolidIcon>
                                  )}
                                </span>
                              </span>
                            </th>

                            <th
                              scope="col"
                              className="px-3 py-2 font-light text-sm text-center text-white dark:text-white tracking-wider"
                            >
                              Action
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {newContentArea.map((content, index) => (
                            <tr
                              key={content.contentAreaId}
                              className={
                                index % 2 === 0 ? "bg-white" : "bg-gray-100"
                              }
                            >
                              <td className="px-3 py-2 capitalize  text-sm text-left font-normal text-gray-500">
                                <Link
                                  className="cursor-pointer font-medium text-indigo-600 hover:text-indigo-500 hover:underline"
                                  to={Constant.EDITCONTENT.replace(
                                    ":id",
                                    content.contentAreaId.toString()
                                  )}
                                >
                                  {content.contentAreaId}
                                </Link>
                              </td>
                              <td className="px-3 py-2  capitalize  text-sm text-left font-normal text-gray-500">
                                {content.name}
                              </td>
                              <td className="px-3 py-2 capitalize  text-sm  text-left font-normal text-gray-500">
                                {content.abbreviation?.toUpperCase()}
                              </td>

                              <td className="px-3 py-2  text-sm text-center font-normal text-gray-700">
                                <div className="flex gap-3 justify-center">
                                  <AllowedTo
                                    perform={Permissions.content_area_modify}
                                  >
                                    <EditIcon
                                      onClick={(e) => handleRedirect(content)}
                                      className="w-4 text-xs font-light cursor-pointer hover:text-secondary-color transaction ease-in-out duration-300"
                                    />
                                  </AllowedTo>

                                  <AllowedTo
                                    perform={Permissions.content_area_delete}
                                  >
                                    <DeleteIcon
                                      onClick={(e) =>
                                        DeleteContentArea(
                                          e,
                                          content.contentAreaId
                                        )
                                      }
                                      className="w-4 text-xs font-light cursor-pointer hover:text-red-500 transaction ease-in-out duration-300"
                                    />
                                  </AllowedTo>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
                <div className="flex justify-between items-center">
                  {newContentArea.length !== 0 ? (
                    <>
                      <Pagination
                        pages={totalPages}
                        setPageNumber={setPageNumber}
                        ref={paginationRef}
                      />
                      <span>
                        <span className="text-sm pr-4 ml-auto text-left-700">
                          Showing {pageNumber} to{" "}
                          {totalRecords < pageSize ? totalRecords : pageSize} of{" "}
                          {totalRecords} results
                        </span>

                        <SelectPageSize
                          setPageSize={setPageSize}
                          type={"second"}
                          ref={pageSizeRef}
                        />
                      </span>
                    </>
                  ) : (
                    <p className="p-2">No Records Found</p>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </AllowedTo>
  );
};

export default Content;
